import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Menu, UnstyledButton, Group, Box, Text, Loader } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Calendar, Check, ChevronRight } from 'lucide-react';

import { epochsApi } from '../../api';
import { eyebrowStyle } from '../../theme';

/**
 * Rychlé přepnutí aktivní epochy z patičky panelu. Aktivní epocha je vždycky
 * nejvýš jedna a mobilní zařízení se hlásí právě k ní, takže je to zásah do
 * provozu — proto se potvrzuje.
 */
export function EpochSwitcher({ compact }: { compact: boolean }) {
  const qc = useQueryClient();
  const epochs = useQuery({ queryKey: ['epochs'], queryFn: epochsApi.getAll });
  const active = epochs.data?.find((e) => e.is_active === 1);

  const activate = useMutation({
    mutationFn: epochsApi.activate,
    onSuccess: () => {
      notifications.show({ message: 'Epocha aktivována', color: 'forest' });
      // Epocha mění všechna data včetně statistik, takže se zneplatní celá cache —
      // vyjmenovávat klíče by znamenalo na některý dřív nebo později zapomenout.
      qc.invalidateQueries();
    },
    onError: (e: any) =>
      notifications.show({
        title: 'Přepnutí se nepovedlo',
        message: e?.response?.data?.error || e.message,
        color: 'red',
      }),
  });

  const label = active?.name ?? (epochs.isLoading ? '…' : 'Žádná aktivní');

  return (
    <Menu position="right-end" withinPortal shadow="md" width={240}>
      <Menu.Target>
        <UnstyledButton
          px={9}
          py={6}
          w="100%"
          aria-label={`Aktivní epocha: ${label}`}
          style={(th) => ({
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            justifyContent: compact ? 'center' : 'flex-start',
            border: `1px solid var(--mantine-color-default-border)`,
            borderRadius: th.radius.sm,
            background: 'var(--mantine-color-body)',
          })}
        >
          <Calendar size={15} color="var(--mantine-primary-color-filled)" style={{ flex: '0 0 15px' }} />
          {!compact && (
            <>
              <Box style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <Text style={eyebrowStyle} c="dimmed" lh={1.2}>Epocha</Text>
                <Text fz="sm" fw={650} lh={1.3} truncate>{label}</Text>
              </Box>
              {activate.isPending
                ? <Loader size={13} />
                : <ChevronRight size={14} color="var(--mantine-color-dimmed)" style={{ flex: '0 0 14px' }} />}
            </>
          )}
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>Přepnout aktivní epochu</Menu.Label>
        {(epochs.data ?? []).map((e) => (
          <Menu.Item
            key={e.id}
            leftSection={e.is_active === 1 ? <Check size={14} /> : <Box w={14} />}
            disabled={e.is_active === 1 || activate.isPending}
            onClick={() => {
              if (window.confirm(
                `Aktivovat epochu ${e.name}? Zařízení v terénu se přepnou na její data.`
              )) activate.mutate(e.id);
            }}
          >
            <Group gap={6} wrap="nowrap">
              <Text fz="sm" truncate>{e.name}</Text>
            </Group>
          </Menu.Item>
        ))}
        {epochs.data?.length === 0 && <Menu.Item disabled>Žádná epocha</Menu.Item>}
      </Menu.Dropdown>
    </Menu>
  );
}
